import React, { FC, useEffect, useState } from 'react';
import { Printer } from '~reducers';
import StepsComponent, { TroubleShootPageContent } from '../Components/StepsComponent';
import ReceiptsNotPrinting from '../Components/ReceiptsNotPrinting';
import BongsNotPrinting from '../Components/BongsNotPrinting';
import ContactSupport from '../Components/ContactSupport';

// Images
import lightPowerImage from '../../Images/star-power-light.png';
import phoneToPower from '../../Images/phone-to-power.png';
import bluetoothSettings from '../../Images/bluetoothsettings.png';
import bluetoothLight from '../../Images/bluetooth-light.png';
import reset from '../../Images/reset.png';
import { useI18n, useLogger, usePrinterApi } from '~Hooks';

interface Props {
  printer: Printer;
  closeGuide: () => void;
  testPrint: () => void;
  assignStation: () => void;
  sessionId: string;
}

type STEPS =
  | 'ISSUE_WITH_QUESTION'
  | 'BLUETOOTH_SETTINGS'
  | 'LIGHTS_QUESTION'
  | 'CHECK_POWER_CABLE'
  | 'TEST_POWER'
  | 'NO_RECEIPT_PRINTING'
  | 'NO_BONGS_PRINTING'
  | 'CONTACT_SUPPORT'
  | 'RESET'
  | 'POWER_CABLE'
  | 'DEVICE_DISTANCE'
  | 'NOT_WORKING'
  | 'RESTART_APP';

const USBTroubleShooting: FC<Props> = ({
  printer,
  closeGuide,
  testPrint,
  assignStation,
  sessionId,
}) => {
  const logger = useLogger('printer-troubleshooting');
  const { i18n } = useI18n();
  const printerApi = usePrinterApi();
  const [currentStep, setCurrentStep] = useState<STEPS>('ISSUE_WITH_QUESTION');

  useEffect(() => {
    logger.info('Troubleshooting step: ' + currentStep, {
      troubleShooting: {
        printerId: printer.id,
        printerName: printerApi.getNameFromId(printer.id),
        model: printer.model,
        createdAt: printer.createdAt,
        lastPrinted: printer.lastPrinted,
        troubleShootingSessionId: sessionId,
      },
    });
  }, [currentStep]);

  const steps: {
    [key in STEPS]: TroubleShootPageContent<STEPS>;
  } = {
    ISSUE_WITH_QUESTION: {
      title: i18n('Printer.TS.IssueWith.Question'),
      buttons: [
        {
          text: i18n('Printer.TS.IssueWith.Receipts'),
          next: 'NO_RECEIPT_PRINTING',
        },
        {
          text: i18n('Printer.TS.IssueWith.KitchenTickets'),
          next: 'NO_BONGS_PRINTING',
        },
        {
          text: i18n('Printer.TS.IssueWith.Everything'),
          next: 'LIGHTS_QUESTION',
        },
      ],
    },
    LIGHTS_QUESTION: {
      title: i18n('Printer.TS.LightsQuestion'),
      images: [
        {
          src: lightPowerImage,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'CHECK_POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'BLUETOOTH_SETTINGS',
        },
      ],
    },
    CHECK_POWER_CABLE: {
      title: i18n('Printer.TS.CablePluggedIn'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'TEST_POWER',
        },
      ],
    },
    TEST_POWER: {
      title: i18n('Printer.TS.PowerWorking'),
      text: i18n('Printer.TS.PowerWorking.Desc'),
      images: [
        {
          src: phoneToPower,
          height: 250,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    BLUETOOTH_SETTINGS: {
      title: i18n('Printer.TS.BT.GoToSettings'),
      text: i18n('Printer.TS.BT.GoToSettings.Desc'),
      images: [
        {
          src: bluetoothSettings,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'NOT_WORKING',
        },
      ],
    },
    NOT_WORKING: {
      title: i18n('Printer.TS.BT.LightsOn'),
      images: [
        {
          src: bluetoothLight,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.No'),
          next: 'POWER_CABLE',
        },
        {
          text: i18n('Printer.Troubleshooting.Yes'),
          next: 'RESTART_APP',
        },
      ],
    },
    RESTART_APP: {
      title: i18n('Printer.TS.BT.Restart'),
      text: i18n('Printer.TS.BT.Restart.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.OK'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'RESET',
        },
      ],
    },
    RESET: {
      title: i18n('Printer.TS.BT.ResetPrinter'),
      text: i18n('Printer.TS.BT.ResetPrinter.Desc'),
      images: [
        {
          src: reset,
        },
      ],
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.Next'),
          next: 'BLUETOOTH_SETTINGS',
        },
      ],
    },
    POWER_CABLE: {
      title: i18n('Printer.TS.BT.PullOutCable'),
      text: i18n('Printer.TS.BT.PullOutCable.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'DEVICE_DISTANCE',
        },
      ],
    },
    DEVICE_DISTANCE: {
      title: i18n('Printer.TS.BT.CheckDistance'),
      text: i18n('Printer.TS.BT.CheckDistance.Desc'),
      buttons: [
        {
          text: i18n('Printer.Troubleshooting.TestPrint'),
          action: 'TEST_PRINT',
        },
        {
          text: i18n('Printer.Troubleshooting.NotWorking'),
          next: 'CONTACT_SUPPORT',
        },
      ],
    },
    NO_RECEIPT_PRINTING: {
      custom: (
        <ReceiptsNotPrinting
          printer={printer}
          closeGuide={closeGuide}
          testPrint={testPrint}
          printingWorks={(works) => setCurrentStep(works ? 'CONTACT_SUPPORT' : 'LIGHTS_QUESTION')}
          sessionId={sessionId}
        />
      ),
    },
    NO_BONGS_PRINTING: {
      custom: (
        <BongsNotPrinting
          printer={printer}
          closeGuide={closeGuide}
          goToContactSupport={() => setCurrentStep('CONTACT_SUPPORT')}
          assignStation={() => {
            closeGuide();
            assignStation();
          }}
          sessionId={sessionId}
        />
      ),
    },
    CONTACT_SUPPORT: {
      custom: <ContactSupport />,
    },
  };

  return (
    <StepsComponent<STEPS>
      steps={steps}
      testPrint={testPrint}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
    />
  );
};

export default USBTroubleShooting;
